import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { APP_VERSION } from "../constants";
import styles from "./Sidebar.module.css";
import { getCurrentTokenAccessInfo } from "../utils/jwt";

interface SidebarProps {
  isMobileMenuOpen?: boolean;
  closeMobileMenu?: () => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isMobileMenuOpen = false, 
  closeMobileMenu,
  isMobile = false 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLinkClick = () => {
    if (isMobile && closeMobileMenu) {
      closeMobileMenu();
    }
  };

  const access = getCurrentTokenAccessInfo();
  const isContentManager = Boolean(
    access?.roles.some((role) => role.toUpperCase() === "CONTENT_MANAGER")
  );
  const canSeeUsers = Boolean(access?.hasPanelAccess);

  return (
    <aside className={`${styles.sidebar} ${isMobile ? styles.sidebarMobile : ''} ${isMobileMenuOpen ? styles.sidebarOpen : ''}`}>
      {/* Logo */}
      <div className={styles.logoSection}>
        <div className={styles.logoContainer}>
          <span className={styles.logoIcon} role="img" aria-label="drum">🥁</span>
          <div>
            <h1 className={styles.logoText}>Drumly</h1>
            <span className={styles.logoSubtext}>Dashboard</span>
          </div>
        </div>
        <span className={styles.versionBadge}>v{APP_VERSION}</span>
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navSection}>
          <span className={styles.navSectionLabel}>Main</span>
          <Link 
            to="/dashboard" 
            className={`${styles.navLink} ${isActive('/dashboard') || isActive('/') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="dashboard">📊</span>
            <span>Dashboard</span>
          </Link>
          {canSeeUsers && (
            <Link 
              to="/users" 
              className={`${styles.navLink} ${isActive('/users') ? styles.navLinkActive : ''}`}
              onClick={handleLinkClick}
            >
              <span className={styles.navIcon} role="img" aria-label="users">👥</span>
              <span>{isContentManager ? "Users (Read-only)" : "Users"}</span>
            </Link>
          )}
          <Link 
            to="/announcements" 
            className={`${styles.navLink} ${isActive('/announcements') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="announcements">📢</span>
            <span>Announcements</span>
          </Link>
          <Link
            to="/studio"
            className={`${styles.navLink} ${isActive('/studio') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="studio">🎛️</span>
            <span>Studio</span>
          </Link>
          <Link 
            to="/song-requests" 
            className={`${styles.navLink} ${isActive('/song-requests') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="requests">📝</span>
            <span>Song Requests</span>
          </Link>
          <Link 
            to="/songs" 
            className={`${styles.navLink} ${isActive('/songs') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="songs">🎵</span>
            <span>Songs</span>
          </Link>
          <Link 
            to="/notifications" 
            className={`${styles.navLink} ${isActive('/notifications') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="notifications">🔔</span>
            <span>Notifications</span>
          </Link>
          <Link 
            to="/health" 
            className={`${styles.navLink} ${isActive('/health') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="health">🩺</span>
            <span>Health</span>
          </Link>
        </div>
      </nav>

      {/* Logout Button */}
      <div className={styles.sidebarFooter}>
        <button onClick={handleLogout} className={styles.logoutButton}>
          <span className={styles.navIcon} role="img" aria-label="logout">🚪</span>
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
